import { PropsWithChildren, useMemo } from 'react';
import { DevCycleProvider } from '@devcycle/react-client-sdk';

import { Configuration, DEFAULT_ENTITIES, DEFAULT_SPACE_ID, EntitiesConfiguration } from '../Configuration';
import { ContentService } from '../ContentService';
import { CMSProviderContext } from '../contexts/CMSContext';
import { Environment } from '../Environment';
import { useAsyncData } from '../hooks';
import { FooterData, footerFallback, HeaderData, headerFallback, MaintenanceBannerData, ModalData } from '../models';

export type CMSProviderProps = {
  /** Access token, must have access to provided environment on space. Must be a Content Delivery Token. **DO NOT USE A CONTENT MANAGEMENT TOKEN** */
  accessToken: string;
  /** Can be provided to override header or footer entity 'internal names' as required */
  entities?: Partial<EntitiesConfiguration>;
  environment: Environment;
  /** Can be provided to override default space ID as required */
  space?: string;
};

const DECYCLE_ENVIRONMENTS: Record<Environment, string> = {
  [Environment.DEV]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_DEV,
  [Environment.TESTING]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_TST,
  [Environment.STAGING]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_STG,
  [Environment.PROD]: import.meta.env.VITE_DEVCYCLE_SDK_KEY_PROD,
};

export const CMSProvider = ({
  accessToken,
  children,
  entities = DEFAULT_ENTITIES,
  environment,
  space = DEFAULT_SPACE_ID,
}: PropsWithChildren<CMSProviderProps>) => {
  // Important that this is memoed correctly as creating a new client
  // will cause refreshes of data automatically (env change/entity ID changes/space/access token)
  const service = useMemo(() => {
    const allEntities: EntitiesConfiguration = { ...DEFAULT_ENTITIES, ...entities };
    const config: Configuration = { accessToken, environment, space };

    return new ContentService(config, allEntities);
  }, [accessToken, entities, environment, space]);

  const footer = useAsyncData<FooterData>({ fetcher: service.getFooter, fallback: footerFallback });
  const header = useAsyncData<HeaderData>({ fetcher: service.getHeader, fallback: headerFallback });
  const maintenanceBanners = useAsyncData<MaintenanceBannerData[]>({ fetcher: service.getMaintenanceBanners });
  const modal = useAsyncData<ModalData | undefined>({ fetcher: service.getModal, fallback: undefined });

  const context = useMemo(() => ({ footer, header, maintenanceBanners, modal }), [footer, header, maintenanceBanners, modal]);

  return (
    <CMSProviderContext.Provider value={context}>
      <DevCycleProvider config={{ sdkKey: DECYCLE_ENVIRONMENTS[environment] }}>{children}</DevCycleProvider>
    </CMSProviderContext.Provider>
  );
};
